/* GASSISA
=========================================================================================
* Descripción: Módulo de Pago de Comisiones por Venta
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 19/04/2026 |                                | Creación
* --------------------------------------------------------------------------------------- 
*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import React, { useState, useMemo, useEffect, forwardRef } from "react";
import { Paper, Typography, Box, Stack, Divider, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import SizeSoftXLayoutForm from "../leyout/SizeSoftXLayoutForm";
import MnuComisionesActions from "./MnuComisionesActions";
import { useRequest } from "../../context/System/RequestContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useVentas } from "../../context/Ventas/VentasContext";
import { useSesion } from "../../context/System/SesionContext";
import { useFormTabLock } from "../../hooks/useFormTabLock";
import { apiPrevendedoresComision, apiComisionesVenta, apiReporteComisiones } from "../../settings/ApiConfig";

const CustomDateInput = forwardRef(function CustomDateInput(props, ref) {
  const { value, onClick, label } = props;

  return (
    <TextField autoComplete="off"
      label={label}
      size="small"
      value={value || ""}
      onClick={onClick}
      inputRef={ref}
      fullWidth
      InputProps={{
        readOnly: true,
      }}
      sx={{ minWidth: 170 }}
    />
  );
});

const normalizeStartDate = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const normalizeEndDate = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const formatDateOnly = (date) => {
  if (!date) return "";
  if (typeof date === "string") {
    return date;
  }
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj?.getTime())) return "";
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  } catch {
    return "";
  }
};

const formatCurrency = (value) => {
  if (value == null) return "$0.00";
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(value);
};

export default function FrmPagoComisiones() {
  const { isLocked: isFormLocked } = useFormTabLock('pago_comisiones', {
    title: 'Pago Comisiones',
    onLockDetected: (info) => {
      console.warn(`El formulario de ${info?.title || 'Pago Comisiones'} ya está abierto en otra pestaña`);
    }
  });

  const { GetRequest, PostRequest, PostFileRequest } = useRequest();
  const { handleOpenAlert } = useLayout();
  const { handleClickRegresarMainDashboard } = useVentas();
  const { usuarioData } = useSesion();
  
  useEffect(() => {
    if (isFormLocked) {
      alert("El formulario de Pago Comisiones ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.");
      setTimeout(() => {
        if(handleClickRegresarMainDashboard) handleClickRegresarMainDashboard();
      }, 900);
    }
  }, [isFormLocked, handleClickRegresarMainDashboard]);

  const [loading, setLoading] = useState(false);
  const [prevendedores, setPrevendedores] = useState([]);
  const [prevendedorSeleccionado, setPrevendedorSeleccionado] = useState(null);
  const [fechaInicio, setFechaInicio] = useState(new Date());
  const [fechaFin, setFechaFin] = useState(new Date());
  const [comisionesData, setComisionesData] = useState(null);

  useEffect(() => {
    cargarPrevendedores();
  }, []);

  const cargarPrevendedores = async () => {
    setLoading(true);
    const result = await GetRequest({
      url: apiPrevendedoresComision,
      loader: "Cargando prevendedores...",
    });

    setLoading(false);

    if (result.hasError) {
      handleOpenAlert("Error al cargar prevendedores: " + result.message);
      return;
    }

    setPrevendedores(result.result || []);
  };

  const handleBuscarComisiones = async () => {
    if (!prevendedorSeleccionado) {
      handleOpenAlert("Selecciona un prevendedor");
      return;
    }

    const body = {
      fechainicio: formatDateOnly(fechaInicio),
      fechafin: formatDateOnly(fechaFin),
      prevendedor: prevendedorSeleccionado.nombre
    };

    setLoading(true);
    const result = await PostRequest({
      url: apiComisionesVenta,
      body: body,
      loader: "Calculando comisiones...",
    });

    setLoading(false);

    if (result.hasError) {
      handleOpenAlert("Error al calcular comisiones: " + result.message);
      return;
    }

    setComisionesData(result.result);
  };

  const opcionesPrevendedores = useMemo(() => {
    return (prevendedores || []).map(p => ({
      ...p,
      label: p.nombre
    }));
  }, [prevendedores]);

  const handleChangeFechaInicio = (date) => {
    if (!date) return;
    setFechaInicio(normalizeStartDate(date));
  };

  const handleChangeFechaFin = (date) => {
    if (!date) return;
    setFechaFin(normalizeEndDate(date));
  };

  const handleRegresar = () => {
    setComisionesData(null);
    setPrevendedorSeleccionado(null);
  };

  const handleNuevo = () => {
    setComisionesData(null);
    setPrevendedorSeleccionado(null);
    setFechaInicio(new Date());
    setFechaFin(new Date());
  };

  const handleGenerarReporte = async () => {
    setLoading(true);
    try {
      const data = {
        fechainicio: formatDateOnly(fechaInicio),
        fechafin: formatDateOnly(fechaFin),
        sucursal: "",
        usuario: usuarioData?.Usuario || "",
        tipousuario: usuarioData?.tipousuario || "ADMINISTRADOR",
      };

      const result = await PostFileRequest({
        url: apiReporteComisiones,
        body: data,
        filename: `ReporteComisiones-${formatDateOnly(fechaInicio)}-${formatDateOnly(fechaFin)}.pdf`,
        openInNewTab: true,
        loader: "Cargando reporte de comisiones...",
      });

      if (!result) throw new Error("No se pudo generar el reporte");
    } catch (error) {
      handleOpenAlert("Error al generar reporte PDF: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const totales = useMemo(() => {
    if (!comisionesData?.detalles) return { ventas: 0, comision: 0 };
    
    const ventas = comisionesData.detalles.reduce((sum, d) => sum + (d.importetotal || 0), 0);
    const comision = comisionesData.detalles.reduce((sum, d) => sum + (d.comisioncalculada || 0), 0);
    
    return { ventas, comision };
  }, [comisionesData]);

  return (
    <SizeSoftXLayoutForm
      titlePage="Pago de Comisiones por Venta"
      buttonActions={
        <MnuComisionesActions 
          onCalcularComisiones={handleBuscarComisiones} 
          handleClickRegresarMainDashboard={handleClickRegresarMainDashboard}
          onNuevo={comisionesData ? handleNuevo : null}
          onGenerarReporte={handleGenerarReporte}
        />
      }
    >
      <Box sx={{ p: 2 }}>
        {!comisionesData ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Filtros de búsqueda</Typography>
            
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Autocomplete
                options={opcionesPrevendedores}
                value={prevendedorSeleccionado}
                onChange={(e, v) => setPrevendedorSeleccionado(v)}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                sx={{ minWidth: 300 }}
                renderInput={(params) => (
                  <TextField autoComplete="off" {...params} label="Prevendedor" />
                )}
              />
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="caption" display="block">Fecha Inicio</Typography>
                <DatePicker
                  selected={fechaInicio}
                  onChange={handleChangeFechaInicio}
                  dateFormat="yyyy-MM-dd"
                  customInput={<CustomDateInput label="Fecha Inicio" />}
                />
              </Box>
              <Box>
                <Typography variant="caption" display="block">Fecha Fin</Typography>
                <DatePicker
                  selected={fechaFin}
                  onChange={handleChangeFechaFin}
                  dateFormat="yyyy-MM-dd"
                  customInput={<CustomDateInput label="Fecha Fin" />}
                />
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />
          </Paper>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Comisión de: {comisionesData.prevendedor?.nombre}
            </Typography>

            <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Periodo</Typography>
                <Typography>
                  {formatDateOnly(comisionesData.fechainicio)} al {formatDateOnly(comisionesData.fechafin)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Porcentaje Comisión</Typography>
                <Typography>{comisionesData.porcentajecomision}%</Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Artículo</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Medida</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Costo</TableCell>
                    <TableCell align="right">Importe</TableCell>
                    <TableCell align="right">Comisión</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(comisionesData.detalles || []).map((detalle, index) => (
                    <TableRow key={index}>
                      <TableCell>{detalle.fechaventa ? formatDateOnly(new Date(detalle.fechaventa)) : ""}</TableCell>
                      <TableCell>{detalle.clavearticulo}</TableCell>
                      <TableCell>{detalle.descripcion}</TableCell>
                      <TableCell align="right">{detalle.medida}</TableCell>
                      <TableCell align="right">{detalle.cantidad}</TableCell>
                      <TableCell align="right">{formatCurrency(detalle.costocliente)}</TableCell>
                      <TableCell align="right">{formatCurrency(detalle.importetotal)}</TableCell>
                      <TableCell align="right">{formatCurrency(detalle.comisioncalculada)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Total Ventas</Typography>
                <Typography variant="h5">{formatCurrency(totales.ventas)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total Comisión</Typography>
                <Typography variant="h5" color="primary">{formatCurrency(totales.comision)}</Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </SizeSoftXLayoutForm>
  );
}